import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { MediaCard } from 'components/Shared/MediaCard'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import TableMessage from 'components/Shared/TableMessage'
import { NoImagesIcon } from 'images'

export default function LibraryGrid({
  mappedItems,
  isLoading,
  isError,
  isFetchingMore,
  hasMore,
  isFetchMoreError,
  hasFilters,
  fetchNextPage,
  retryFetchMore,
  selectiveMode = false,
  selectedMedia = [],
  setSelectedMedia = () => {},
  onPreviewClick,
  getActionMenuItems,
  enableGroupHighlight = true,
  enableVariantClick = true,
  emptyDescription,
  intersectionRootRef,
}) {
  const [focusedGroupId, setFocusedGroupId] = useState(null)
  const { t } = useTranslation()
  const loadMoreRef = useRef(null)
  const gridRef = useRef(null)

  useEffect(() => {
    if (!hasMore || isLoading || isFetchingMore || isFetchMoreError) {
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage()
        }
      },
      {
        root: intersectionRootRef?.current ?? null,
        rootMargin: '200px',
        threshold: 0,
      },
    )

    const target = loadMoreRef.current

    if (target) {
      observer.observe(target)
    }

    return () => {
      if (target) {
        observer.unobserve(target)
      }
      observer.disconnect()
    }
  }, [fetchNextPage, hasMore, intersectionRootRef, isFetchMoreError, isFetchingMore, isLoading, mappedItems.length])

  useEffect(() => {
    if (!focusedGroupId) {
      return undefined
    }

    const handleOutsideClick = (event) => {
      if (gridRef.current?.contains(event.target)) {
        return
      }
      setFocusedGroupId(null)
    }

    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [focusedGroupId])

  useEffect(() => {
    if (selectiveMode || !enableVariantClick) {
      setFocusedGroupId(null)
    }
  }, [selectiveMode, enableVariantClick])

  if (isLoading) {
    return <LoadingSpinner text={t('common.loading')} className="library__status full-height" />
  }

  if (isError && !mappedItems.length) {
    return (
      <TableMessage
        variant="error"
        title={t('Library.listMessage.error.title')}
        description={t('Library.listMessage.error.description')}
        className="full-height"
      />
    )
  }

  if (!mappedItems.length) {
    return (
      <TableMessage
        variant="empty"
        description={emptyDescription ?? (hasFilters ? t('Library.listMessage.empty.filters') : t('Library.listMessage.empty.noImages'))}
        icon={<NoImagesIcon width={24} height={24} />}
        className="full-height"
      />
    )
  }

  const isGroupFocused = enableGroupHighlight && !selectiveMode && Boolean(focusedGroupId)

  return (
    <>
      <div className="library-list__grid" ref={gridRef}>
        {mappedItems.map(({ item, mediaLabel }) => {
          const isInFocusedGroup = item.group_id === focusedGroupId
          const actionMenuItems = getActionMenuItems?.(item) ?? []

          return (
            <MediaCard
              key={item.id}
              item={item}
              showLabel
              mediaLabel={mediaLabel}
              selectiveMode={selectiveMode}
              selectedMedia={selectedMedia}
              setSelectedMedia={setSelectedMedia}
              onVariantClick={!selectiveMode && enableVariantClick && mediaLabel.variant === 'uploaded-variants'
                ? () => setFocusedGroupId((prev) => (prev === item.group_id ? null : item.group_id))
                : undefined}
              isGroupHighlighted={isGroupFocused && isInFocusedGroup}
              isDimmed={isGroupFocused && !isInFocusedGroup}
              onDimmedClick={isGroupFocused && !isInFocusedGroup
                ? () => setFocusedGroupId(null)
                : undefined}
              onPreviewClick={onPreviewClick ? () => onPreviewClick(item) : undefined}
              actionMenuItems={actionMenuItems}
            />
          )
        })}
      </div>
      {isFetchingMore && <LoadingSpinner text={t('common.loading')} className="library__status" />}
      {isFetchMoreError && (
        <div className="library-list__load-more-error">
          <p className="library-list__load-more-error-text">
            {t('Library.listMessage.error.loadMore')}
          </p>
          <button
            type="button"
            className="library-list__load-more-retry"
            onClick={retryFetchMore}
          >
            {t('common.retry')}
          </button>
        </div>
      )}
      {hasMore && !isFetchMoreError && (
        <div className="library-list__load-more-trigger" ref={loadMoreRef} aria-hidden="true" />
      )}
    </>
  )
}

LibraryGrid.propTypes = {
  mappedItems: PropTypes.arrayOf(PropTypes.shape({
    item: PropTypes.object.isRequired,
    mediaLabel: PropTypes.object.isRequired,
  })).isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  isFetchingMore: PropTypes.bool,
  hasMore: PropTypes.bool,
  isFetchMoreError: PropTypes.bool,
  hasFilters: PropTypes.bool,
  fetchNextPage: PropTypes.func.isRequired,
  retryFetchMore: PropTypes.func.isRequired,
  selectiveMode: PropTypes.bool,
  selectedMedia: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setSelectedMedia: PropTypes.func,
  onPreviewClick: PropTypes.func,
  getActionMenuItems: PropTypes.func,
  enableGroupHighlight: PropTypes.bool,
  enableVariantClick: PropTypes.bool,
  emptyDescription: PropTypes.string,
  intersectionRootRef: PropTypes.shape({
    current: PropTypes.any,
  }),
}
